import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPasskeys, deletePasskey } from '../services/passkeyService';

/**
 * PasskeyManager Component
 * Displays and manages user's registered passkeys in Profile page
 */
export const PasskeyManager = () => {
  const navigate = useNavigate();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchPasskeys();
  }, []);

  const fetchPasskeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const keys = await listPasskeys();
      setPasskeys(keys);
    } catch (err) {
      console.error('Fetch passkeys error:', err);
      // Gracefully handle errors - treat as empty list
      // This is common for OAuth users who haven't added passkeys yet
      setPasskeys([]);
      // Only show error for actual server errors (not just "no passkeys")
      if (err.message && !err.message.includes('404') && !err.message.includes('not found')) {
        setError('Unable to load passkeys. Please try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (passkeyId) => {
    if (!window.confirm('Are you sure you want to remove this passkey? You can always add it back later.')) {
      return;
    }

    try {
      setDeleting(passkeyId);
      await deletePasskey(passkeyId);
      // Refresh list
      await fetchPasskeys();
    } catch (err) {
      console.error('Delete passkey error:', err);
      alert('Failed to delete passkey. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-brown-900 mb-4">Your Passkeys</h3>
        <div className="text-center py-4">
          <div className="spinner-border animate-spin inline-block w-6 h-6 border-3 rounded-full text-brown-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-brown-600 text-sm">Loading passkeys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brown-900">Your Passkeys</h3>
        <button
          onClick={() => navigate('/passkey-setup')}
          className="bg-brown-600 hover:bg-brown-700 text-white font-medium py-1 px-3 rounded-full text-sm"
        >
          + Add Passkey
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {passkeys.length === 0 ? (
        <div className="text-center py-6">
          <div className="text-4xl mb-3">🔐</div>
          <p className="text-brown-600 mb-2">No passkeys set up yet</p>
          <p className="text-sm text-brown-500 mb-4">
            Add a passkey to sign in with Face ID, Touch ID, or Windows Hello
          </p>
          <button
            onClick={() => navigate('/passkey-setup')}
            className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded-full text-sm"
          >
            Set Up Your First Passkey
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {passkeys.map((passkey) => (
            <div
              key={passkey.id}
              className="border border-brown-200 rounded-lg p-4 hover:bg-brown-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-1">
                    <span className="text-2xl mr-2">
                      {passkey.deviceName?.toLowerCase().includes('iphone') ? '📱' :
                       passkey.deviceName?.toLowerCase().includes('mac') ? '💻' :
                       passkey.deviceName?.toLowerCase().includes('windows') ? '🖥️' :
                       passkey.deviceName?.toLowerCase().includes('android') ? '📱' :
                       '🔐'}
                    </span>
                    <div>
                      <h4 className="font-semibold text-brown-800">
                        {passkey.deviceName || 'Unnamed Device'}
                      </h4>
                      {passkey.backupState && (
                        <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          ☁️ Synced
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-brown-500 space-y-0.5">
                    <div>Added: {formatDate(passkey.createdAt)}</div>
                    {passkey.lastUsedAt && (
                      <div>Last used: {formatDate(passkey.lastUsedAt)}</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(passkey.id)}
                  disabled={deleting === passkey.id}
                  className="ml-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-1 px-3 rounded-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting === passkey.id ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          <strong>💡 Tip:</strong> Passkeys are more secure than passwords and sync across your devices automatically.
          {passkeys.length > 0 && ' You can add passkeys for multiple devices.'}
        </p>
      </div>
    </div>
  );
};

export default PasskeyManager;
